import { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Flex, FormControl, FormLabel, Grid, HStack,
  Input, Select, Table, Tbody, Td, Th, Thead, Tr,
  TableContainer, VStack, Text, Badge, Collapse,
  Divider,
} from '@chakra-ui/react';
import { getAllRateCard, createPocoRateCard, updatePocoRateCard, deletePocoRateCard } from '../../Http/Index';
import { ShowAlert } from '../../GlobalComponent/Alerts/Alert';

const AMBULANCE_TYPES = ['BLS-EECO/Bolero', 'BLS-Tempo/Tata Winger', 'ALS (with paramedic)-Tempo/Tata Winger', 'ALS (with doctor/EMT)-Tempo/Tata Winger'];

const defaultCharges = {
  wheelchairCharge: 0, spineBoardCharge: 0, oxygenCharge: 0, waitingCharge: 0,
  helperCharge: 0, doctorOnBoardCharge: 0, acCharge: 0, stretcherCharge: 0, paramedicCharge: 0,
};

const emptyRow = () => ({ distanceRange: '', rateType: 'flat', prices: null as any });

interface AmbulanceTypeEntry {
  ambulanceType: string;
  rows: { distanceRange: string; rateType: string; prices: any }[];
}

function PocoAmbulanceRateCard({ clearFormFive, setClearFormFive }: { clearFormFive: boolean; setClearFormFive: any }) {
  const [allRateCards, setAllRateCards] = useState<any[]>([]);

  // The record currently being created/edited — holds the DB _id once persisted
  const [activeRecord, setActiveRecord] = useState<any>(null);
  // ref so async callbacks always see latest value
  const activeRecordRef = useRef<any>(null);
  const setActive = (rc: any) => { activeRecordRef.current = rc; setActiveRecord(rc); };

  const [rateCardName, setRateCardName] = useState('');
  const [additionalCharges, setAdditionalCharges] = useState({ ...defaultCharges });
  const [typeEntries, setTypeEntries] = useState<AmbulanceTypeEntry[]>([]);

  // Sub-form
  const [subAmbulanceType, setSubAmbulanceType] = useState('');
  const [subRows, setSubRows] = useState([emptyRow()]);
  const [expandedEntry, setExpandedEntry] = useState<string | null>(null);
  const [editingType, setEditingType] = useState<string | null>(null);

  const [savingType, setSavingType] = useState(false);
  const [savingFinal, setSavingFinal] = useState(false);

  useEffect(() => {
    if (clearFormFive) { handleClear(); setClearFormFive(!clearFormFive); }
    // eslint-disable-next-line
  }, [clearFormFive]);

  const fetchAll = async () => {
    try {
      const res = await getAllRateCard();
      const data = res?.data?.data || res?.data || [];
      setAllRateCards(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchAll(); }, []);

  // ── Sub-form helpers ──

  const handleSubRowChange = (index: number, field: string, value: any) => {
    const updated = [...subRows];
    updated[index] = { ...updated[index], [field]: field === 'prices' ? +value : value };
    setSubRows(updated);
  };

  const handleSubInclude = (index: number) => {
    const updated = [...subRows];
    let val = updated[index].distanceRange.trim();
    if (!val) return;
    if (val.toLowerCase().startsWith('above')) {
      // Ensure capital A and ends with ' km'
      val = 'Above' + val.slice(5);
      if (!val.endsWith(' km')) val = `${val} km`;
    } else if (!val.endsWith(' km')) {
      val = `${val} km`;
    }
    updated[index].distanceRange = val;
    setSubRows(updated);
  };

  const resetSubForm = () => {
    setSubAmbulanceType('');
    setSubRows([emptyRow()]);
    setEditingType(null);
  };

  // ── Save a single ambulance type immediately to DB ──
  const handleSaveTypeEntry = async () => {
    if (!subAmbulanceType) return ShowAlert('warning', 'Select an ambulance type');
    if (!rateCardName.trim()) return ShowAlert('warning', 'Enter a rate card name before saving a type');
    const invalidRow = subRows.findIndex((r) => !r.distanceRange.trim() || !r.rateType || r.prices == null);
    if (invalidRow !== -1) return ShowAlert('warning', 'Fill in distance range, rate type and price for all rows');

    setSavingType(true);
    try {
      const current = activeRecordRef.current;

      if (!current) {
        // First type — create the record
        const res = await createPocoRateCard({
          rateCardName,
          ambulanceType: [subAmbulanceType],
          rateCards: { [subAmbulanceType]: subRows },
          ...additionalCharges,
        });
        const created = res?.data?.data || res?.data;
        setActive(created);
        setTypeEntries([{ ambulanceType: subAmbulanceType, rows: subRows }]);
        ShowAlert('success', `${subAmbulanceType} saved`);
      } else {
        // Subsequent types or update — patch the existing record
        const existingEntries = editingType
          ? typeEntries.map((e) => e.ambulanceType === editingType ? { ambulanceType: subAmbulanceType, rows: subRows } : e)
          : [...typeEntries, { ambulanceType: subAmbulanceType, rows: subRows }];

        const rateCardsMap: Record<string, any[]> = {};
        const ambulanceTypes: string[] = [];
        existingEntries.forEach(({ ambulanceType, rows }) => {
          rateCardsMap[ambulanceType] = rows;
          ambulanceTypes.push(ambulanceType);
        });

        const res = await updatePocoRateCard(current._id, {
          rateCardName,
          ambulanceType: ambulanceTypes,
          rateCards: rateCardsMap,
        });
        const updated = res?.data?.data || res?.data;
        setActive(updated);
        setTypeEntries(existingEntries);
        ShowAlert('success', editingType ? `${subAmbulanceType} updated` : `${subAmbulanceType} saved`);
      }

      resetSubForm();
      fetchAll();
    } catch (e: any) {
      ShowAlert('warning', e?.response?.data?.message || 'Error saving type');
    } finally {
      setSavingType(false);
    }
  };

  const handleEditTypeEntry = (entry: AmbulanceTypeEntry) => {
    setEditingType(entry.ambulanceType);
    setSubAmbulanceType(entry.ambulanceType);
    setSubRows(entry.rows.map((r) => ({ ...r })));
    setExpandedEntry(null);
  };

  const handleRemoveTypeEntry = async (ambulanceType: string) => {
    const current = activeRecordRef.current;
    if (!current) {
      setTypeEntries(typeEntries.filter((e) => e.ambulanceType !== ambulanceType));
      return;
    }
    const remaining = typeEntries.filter((e) => e.ambulanceType !== ambulanceType);
    const rateCardsMap: Record<string, any[]> = {};
    remaining.forEach(({ ambulanceType: t, rows }) => { rateCardsMap[t] = rows; });
    try {
      const res = await updatePocoRateCard(current._id, {
        ambulanceType: remaining.map((e) => e.ambulanceType),
        rateCards: rateCardsMap,
      });
      setActive(res?.data?.data || res?.data);
      setTypeEntries(remaining);
      if (expandedEntry === ambulanceType) setExpandedEntry(null);
      if (editingType === ambulanceType) resetSubForm();
      fetchAll();
    } catch (e: any) {
      ShowAlert('warning', e?.response?.data?.message || 'Error removing type');
    }
  };

  // ── Final save: persist rate card name + additional charges ──
  const handleSubmit = async () => {
    if (!rateCardName.trim()) return ShowAlert('warning', 'Enter rate card name');
    const current = activeRecordRef.current;
    if (!current) return ShowAlert('warning', 'Add at least one ambulance type first');

    setSavingFinal(true);
    try {
      await updatePocoRateCard(current._id, { rateCardName, ...additionalCharges });
      ShowAlert('success', 'Rate card saved');
      handleClear();
      fetchAll();
    } catch (e: any) {
      ShowAlert('warning', e?.response?.data?.message || 'Error saving rate card');
    } finally {
      setSavingFinal(false);
    }
  };

  const handleEdit = (rc: any) => {
    setActive(rc);
    setRateCardName(rc.rateCardName || '');
    setAdditionalCharges({
      wheelchairCharge: rc.wheelchairCharge ?? 0,
      spineBoardCharge: rc.spineBoardCharge ?? 0,
      oxygenCharge: rc.oxygenCharge ?? 0,
      waitingCharge: rc.waitingCharge ?? 0,
      helperCharge: rc.helperCharge ?? 0,
      doctorOnBoardCharge: rc.doctorOnBoardCharge ?? 0,
      acCharge: rc.acCharge ?? 0,
      stretcherCharge: rc.stretcherCharge ?? 0,
      paramedicCharge: rc.paramedicCharge ?? 0,
    });
    const rateCardsRaw = rc.rateCards instanceof Map ? Object.fromEntries(rc.rateCards) : (rc.rateCards || {});
    const uniqueTypes = Array.from(new Set(rc.ambulanceType as string[])) as string[];
    const entries: AmbulanceTypeEntry[] = uniqueTypes
      .filter((amb) => rateCardsRaw[amb] !== undefined)
      .map((amb) => ({ ambulanceType: amb, rows: rateCardsRaw[amb] || [emptyRow()] }));
    Object.keys(rateCardsRaw).forEach((key) => {
      if (!entries.some((e) => e.ambulanceType === key))
        entries.push({ ambulanceType: key, rows: rateCardsRaw[key] || [emptyRow()] });
    });
    setTypeEntries(entries);
    resetSubForm();
    setExpandedEntry(null);
  };

  const handleFixDuplicates = async (rc: any) => {
    const rateCardsRaw = rc.rateCards instanceof Map ? Object.fromEntries(rc.rateCards) : (rc.rateCards || {});
    const uniqueTypes = Array.from(new Set(rc.ambulanceType as string[])) as string[];
    const cleanTypes = uniqueTypes.filter((t) => rateCardsRaw[t] !== undefined);
    const cleanRateCards: Record<string, any[]> = {};
    cleanTypes.forEach((t) => { cleanRateCards[t] = rateCardsRaw[t]; });
    try {
      await updatePocoRateCard(rc._id, { ambulanceType: cleanTypes, rateCards: cleanRateCards });
      ShowAlert('success', `"${rc.rateCardName}" duplicates removed`);
      fetchAll();
    } catch (e: any) {
      ShowAlert('warning', e?.response?.data?.message || 'Error fixing duplicates');
    }
  };

  const hasDuplicateTypes = (rc: any) => {
    const types: string[] = rc.ambulanceType || [];
    return types.length !== new Set(types).size;
  };

  const handleDelete = async (id: string) => {
    try {
      await deletePocoRateCard(id);
      ShowAlert('success', 'Rate card deleted');
      fetchAll();
      if (activeRecordRef.current?._id === id) handleClear();
    } catch (e: any) {
      ShowAlert('warning', e?.response?.data?.message || 'Error deleting rate card');
    }
  };

  const handleClear = () => {
    setActive(null);
    setRateCardName('');
    setTypeEntries([]);
    resetSubForm();
    setAdditionalCharges({ ...defaultCharges });
    setExpandedEntry(null);
  };

  const availableTypes = AMBULANCE_TYPES.filter(
    (t) => !typeEntries.some((e) => e.ambulanceType === t) || t === editingType
  );

  const isEditing = !!activeRecord;

  return (
    <Box display="flex" justifyContent="space-between" w="100%">

      {/* Left: Form */}
      <Box p={4} w="50%">
        <Box as="form" p={4} borderWidth={1} borderRadius="md" mb={4}>
          <VStack spacing={4} align="stretch">

            <FormControl>
              <FormLabel>Rate Card Name</FormLabel>
              <Input
                value={rateCardName}
                onChange={(e) => setRateCardName(e.target.value)}
                placeholder="e.g., Dial4242"
              />
            </FormControl>

            {/* ── Saved Ambulance Types ── */}
            {typeEntries.length > 0 && (
              <Box borderWidth={1} borderRadius="md" overflow="hidden">
                <Box bg="teal.50" px={3} py={2} borderBottomWidth={1}>
                  <Text fontWeight="bold" fontSize="sm" color="teal.700">
                    Saved Types ({typeEntries.length})
                  </Text>
                </Box>
                <VStack spacing={0} align="stretch" divider={<Divider />}>
                  {typeEntries.map(({ ambulanceType, rows }) => {
                    const isBeingEdited = editingType === ambulanceType;
                    return (
                      <Box key={ambulanceType}>
                        <HStack
                          px={3} py={2}
                          justifyContent="space-between"
                          bg={isBeingEdited ? 'blue.50' : 'white'}
                          _hover={{ bg: isBeingEdited ? 'blue.50' : 'gray.50' }}
                        >
                          <HStack spacing={2} flex={1} minW={0}>
                            <Badge colorScheme={isBeingEdited ? 'blue' : 'teal'} fontSize="xs" flexShrink={0}>
                              {rows.length} {rows.length === 1 ? 'row' : 'rows'}
                            </Badge>
                            <Text fontSize="sm" color={isBeingEdited ? 'blue.700' : 'gray.700'} isTruncated>
                              {ambulanceType}
                            </Text>
                            {isBeingEdited && <Badge colorScheme="blue" fontSize="xs">editing</Badge>}
                          </HStack>
                          <HStack spacing={1} flexShrink={0}>
                            {!isBeingEdited && (
                              <>
                                <Button size="xs" variant="ghost" colorScheme="gray"
                                  onClick={() => setExpandedEntry(expandedEntry === ambulanceType ? null : ambulanceType)}>
                                  {expandedEntry === ambulanceType ? 'Hide' : 'View'}
                                </Button>
                                <Button size="xs" variant="ghost" colorScheme="blue"
                                  onClick={() => handleEditTypeEntry({ ambulanceType, rows })}>
                                  Edit
                                </Button>
                              </>
                            )}
                            <Button size="xs" variant="ghost" colorScheme="red"
                              onClick={() => handleRemoveTypeEntry(ambulanceType)}>
                              Remove
                            </Button>
                          </HStack>
                        </HStack>

                        <Collapse in={expandedEntry === ambulanceType && !isBeingEdited} animateOpacity>
                          <Box px={3} pb={3} bg="gray.50">
                            <Table size="xs" variant="simple">
                              <Thead>
                                <Tr>
                                  <Th fontSize="10px">Distance Range</Th>
                                  <Th fontSize="10px">Rate Type</Th>
                                  <Th fontSize="10px" isNumeric>Price (₹)</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {rows.map((r, i) => (
                                  <Tr key={i}>
                                    <Td fontSize="xs">{r.distanceRange}</Td>
                                    <Td fontSize="xs">{r.rateType}</Td>
                                    <Td fontSize="xs" isNumeric>{r.prices}</Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </VStack>
              </Box>
            )}

            {/* ── Sub-form: Add / Edit ambulance type ── */}
            <Box borderWidth={1} borderRadius="md" overflow="hidden"
              borderColor={editingType ? 'blue.300' : 'gray.200'}>
              <Box bg={editingType ? 'blue.50' : 'gray.100'} px={3} py={2} borderBottomWidth={1}>
                <HStack justifyContent="space-between">
                  <Text fontWeight="bold" fontSize="sm" color={editingType ? 'blue.700' : 'gray.600'} textTransform="uppercase">
                    {editingType
                      ? `Editing: ${editingType}`
                      : availableTypes.length === 0
                        ? 'All ambulance types added'
                        : 'Add Ambulance Type'}
                  </Text>
                  {editingType && (
                    <Button size="xs" variant="ghost" colorScheme="gray" onClick={resetSubForm}>Cancel</Button>
                  )}
                </HStack>
              </Box>

              {(availableTypes.length > 0 || editingType) ? (
                <>
                  <Box px={3} py={3} borderBottomWidth={1} bg="white">
                    <FormControl>
                      <FormLabel fontSize="sm">Ambulance Type</FormLabel>
                      <Select
                        placeholder="Select Ambulance Type"
                        value={subAmbulanceType}
                        onChange={(e) => setSubAmbulanceType(e.target.value)}
                        borderColor={editingType ? 'blue.400' : 'black'}
                        _hover={{ borderColor: editingType ? 'blue.500' : 'black' }}
                        focusBorderColor={editingType ? 'blue.400' : 'black'}
                        isDisabled={!!editingType}
                      >
                        {availableTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                      </Select>
                    </FormControl>
                  </Box>

                  <VStack spacing={0} align="stretch">
                    {subRows.map((row, index) => (
                      <Box key={index} px={3} py={3}
                        bg={index % 2 === 0 ? 'white' : 'gray.50'}
                        borderBottomWidth={1} borderColor="gray.100">
                        <HStack spacing={3} alignItems="flex-end">
                          <FormControl>
                            <FormLabel fontSize="sm">Distance Range</FormLabel>
                            <Input size="sm" value={row.distanceRange}
                              onChange={(e) => handleSubRowChange(index, 'distanceRange', e.target.value)}
                              onBlur={() => handleSubInclude(index)}
                              placeholder="e.g, 0-5 or Above 100"
                              bg="white" borderColor="black"
                              _hover={{ borderColor: 'black' }} focusBorderColor="black" />
                          </FormControl>
                          <FormControl>
                            <FormLabel fontSize="sm">Rate Type</FormLabel>
                            <Select size="sm" bg="white" value={row.rateType}
                              onChange={(e) => handleSubRowChange(index, 'rateType', e.target.value)}
                              borderColor="black" _hover={{ borderColor: 'black' }} focusBorderColor="black">
                              <option value="flat">Flat</option>
                              <option value="per km">Per km</option>
                            </Select>
                          </FormControl>
                          <FormControl>
                            <FormLabel fontSize="sm">Price</FormLabel>
                            <Input size="sm" type="number" bg="white" value={row.prices ?? ''}
                              onChange={(e) => handleSubRowChange(index, 'prices', e.target.value)}
                              placeholder="0" borderColor="black"
                              _hover={{ borderColor: 'black' }} focusBorderColor="black" />
                          </FormControl>
                          {index !== 0 && (
                            <Button colorScheme="red" size="sm" width="40%"
                              onClick={() => setSubRows(subRows.filter((_, i) => i !== index))}>
                              Remove
                            </Button>
                          )}
                        </HStack>
                      </Box>
                    ))}
                  </VStack>

                  <Box px={3} py={2} bg="gray.50" borderTopWidth={1}>
                    <HStack>
                      <Button size="sm" colorScheme="blue"
                        onClick={() => setSubRows([...subRows, emptyRow()])}>
                        Add Range
                      </Button>
                      <Button size="sm" colorScheme={editingType ? 'blue' : 'teal'}
                        onClick={handleSaveTypeEntry}
                        isLoading={savingType}
                        loadingText="Saving...">
                        {editingType ? '✓ Update Type' : '✓ Save Type Rates'}
                      </Button>
                    </HStack>
                  </Box>
                </>
              ) : (
                <Box px={3} py={4} bg="white">
                  <Text fontSize="sm" color="gray.500" textAlign="center">
                    All 4 ambulance types have been added.
                  </Text>
                </Box>
              )}
            </Box>

            {/* ── Additional Charges ── */}
            <Box borderWidth={1} borderRadius="md" overflow="hidden">
              <Box bg="gray.100" px={3} py={2} borderBottomWidth={1}>
                <HStack justifyContent="space-between">
                  <Box fontWeight="bold" fontSize="sm" color="gray.600" textTransform="uppercase" flex={1}>
                    Additional Rate Card Charge
                  </Box>
                  <Box fontWeight="bold" fontSize="sm" color="gray.600" textTransform="uppercase" w="100px" textAlign="right">
                    Amount (₹)
                  </Box>
                </HStack>
              </Box>
              <Grid templateColumns="1fr 1fr">
                {(Object.keys(additionalCharges) as (keyof typeof additionalCharges)[]).map((key, i) => (
                  <HStack key={key} px={3} py={2} justifyContent="space-between" bg="white"
                    borderBottomWidth={1} borderRightWidth={i % 2 === 0 ? 1 : 0} borderColor="gray.100">
                    <Box fontSize="sm" color="gray.700" flex={1}>{key.replace(/([A-Z])/g, ' $1').trim()}</Box>
                    <Input type="number"
                      value={additionalCharges[key] === 0 ? '' : additionalCharges[key]}
                      onChange={(e) => setAdditionalCharges((prev) => ({ ...prev, [key]: +e.target.value }))}
                      placeholder="0" size="xs" w="80px" textAlign="right"
                      borderColor="black" _hover={{ borderColor: 'black' }} focusBorderColor="black" />
                  </HStack>
                ))}
              </Grid>
            </Box>

            <HStack spacing={3}>
              <Button colorScheme="teal" flex={1} onClick={handleSubmit}
                isLoading={savingFinal} loadingText="Saving..."
                isDisabled={!activeRecord}>
                {isEditing ? 'Update' : 'Save Rate Card'}
              </Button>
              <Button colorScheme="gray" flex={1} onClick={handleClear}>Clear</Button>
            </HStack>

            {activeRecord && (
              <Text fontSize="xs" color="teal.600" textAlign="center">
                Types are saved automatically. Use "Save Rate Card" to update name &amp; additional charges.
              </Text>
            )}

          </VStack>
        </Box>
      </Box>

      {/* ── Right: Rate Cards List ── */}
      <Box pt={4} px={2} w="50%">
        <TableContainer borderWidth={1} borderRadius="md">
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>Types</Th>
                <Th>Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {allRateCards.map((rc: any) => {
                const uniqueDisplayTypes = Array.from(new Set(rc.ambulanceType as string[])) as string[];
                const dirty = hasDuplicateTypes(rc);
                return (
                  <Tr key={rc._id} bg={activeRecord?._id === rc._id ? 'teal.50' : undefined}>
                    <Td fontWeight={activeRecord?._id === rc._id ? 'bold' : undefined}>{rc.rateCardName}</Td>
                    <Td>
                      <Flex gap={1} flexWrap="wrap">
                        {uniqueDisplayTypes.map((t: string) => (
                          <Badge key={t} colorScheme="teal" fontSize="10px">{t}</Badge>
                        ))}
                        {dirty && <Badge colorScheme="red" fontSize="10px">has duplicates</Badge>}
                      </Flex>
                    </Td>
                    <Td>
                      <Flex gap={2} flexWrap="wrap">
                        {dirty && (
                          <Button size="sm" colorScheme="orange" onClick={() => handleFixDuplicates(rc)}>Fix</Button>
                        )}
                        <Button size="sm" onClick={() => handleEdit(rc)}>Edit</Button>
                        <Button size="sm" colorScheme="red" onClick={() => handleDelete(rc._id)}>Delete</Button>
                      </Flex>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>

    </Box>
  );
}

export default PocoAmbulanceRateCard;
